import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  formatAmountParts,
  formatDate,
  formatInteger,
  formatMonthName,
  formatPercent,
  formatSigned,
  formatTime,
  TV_AUTOSCROLL_DEFAULT,
  TV_AUTOSCROLL_PX_PER_SECOND,
  TV_DASHBOARD_REFRESH_CHECK_MS,
  TV_DASHBOARD_REFRESH_MS,
  TV_DASHBOARD_STALE_MINUTES,
  TV_SCREEN_INFO,
  TV_SELLER_ROWS,
  TV_ZOOM_DEFAULT_PCT,
  type TvAutoscroll,
  type TvDashboardResponse,
  type TvDashboardSettings,
  type TvPace,
  type TvRefreshStatus,
  type TvScreen,
} from '@codepass/shared';
import { ApiError, apiRequest } from '@/lib/api';
import { cn } from '@/lib/cn';
import { useElementSize } from '@/lib/use-element-size';

/**
 * Storskaermen - OneCodes faelles vaekstmaal paa vaeggen. Specen er
 * docs/dashboard/1-1-Storskaerm-Dashboard-Designnotat.md, layoutet er mockuppen ved siden af, og
 * siden foelger den maal for maal.
 *
 * **Kun tal.** Ingen menu, ingen brugermenu, ingen links og ingen knapper: siden ligger uden for
 * AppShell (se App.tsx), og intet her kan klikkes. Alle tal er regnet i API'et; her formateres og
 * tegnes de.
 *
 * **Adgangen er adressen.** Skaermen aabner `/dashboard/tv?token=...`, og tokenet sendes til
 * API'et i en header - ikke i query'en, som ville staa i hver requestlog. API'et afgoer resten:
 * tokenet skal passe, og kaldet skal komme fra den godkendte IP. Se apps/api/src/lib/tv-access.ts.
 *
 * **Tegnet til 1920 x 1080 og skaleret til skaermen.** Et TV har én opløsning og ingen rullebjaelke,
 * og en layout der flyder, ville se forskellig ud paa hver skaerm. Scenen skaleres i stedet som et
 * lysbillede, saa alt staar samme sted uanset stoerrelse.
 *
 * **Tallene skifter, skaermen gør ikke.** En opdatering udskifter tallene og tidsstemplet paa
 * stedet. "Henter tal …" staar der kun, foer skaermen nogensinde har haft tal at vise - ogsaa efter
 * en genindlaesning, se `readSnapshot`.
 *
 * **"Opdater skærmen nu" paa /admin/tv-dashboard naar frem inden for ti sekunder.** Skaermen
 * spoerger saa ofte, om en admin har bedt om nye tal, og henter med det samme, naar bestillingen
 * er nyere end de tal, den viser. Ellers venter den paa sine fem minutter.
 *
 * **Tre skaerme, én skal** (22-10-2026). Storskaerm 2 og 3 - TvProjects.tsx og TvInternal.tsx -
 * aabnes, hentes, gemmes og opdateres paa samme maade, hver med sit eget token: se `TvScreenPage`.
 */

const STAGE_WIDTH = 1920;
const STAGE_HEIGHT = 1080;

/** What every screen's answer carries besides its numbers. */
export interface TvScreenData {
  generated_at: string;
  updated_at: string;
  stale: boolean;
  display: TvDashboardSettings;
}

/**
 * The last numbers the screen showed, kept in the browser, so a reload - a kiosk browser that
 * restarts, a new deploy, a TV switched on in the morning - draws them at once and updates them in
 * place, instead of going blank on "Henter tal …" until the API answers. Only on the screen: the
 * preview on /admin/tv-dashboard has its own query. Removed when the API refuses the screen.
 * Screen 1 keeps the key it had before there were three.
 */
function snapshotKey(screen: TvScreen): string {
  return screen === 'growth'
    ? 'codepass.tv-dashboard.snapshot'
    : `codepass.tv-dashboard.${screen}.snapshot`;
}

/**
 * Every top-level field of the response. A snapshot saved by an older build that lacks one is
 * thrown away rather than drawn: a field the view reads and cannot find would take the whole
 * screen down. The `satisfies` makes a new field in the type a compile error here.
 */
const RESPONSE_FIELDS = Object.keys({
  generated_at: true,
  updated_at: true,
  stale: true,
  display: true,
  month: true,
  quarter: true,
  year: true,
  utilization: true,
  order_intake: true,
  pipeline: true,
  order_book: true,
  sales_per_seller: true,
  ready_to_invoice: true,
  recurring_revenue: true,
} satisfies Record<keyof TvDashboardResponse, true>);

function readSnapshot<T>(key: string, fields: readonly string[]): T | undefined {
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return undefined;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return undefined;
    return fields.every((field) => field in parsed) ? (parsed as T) : undefined;
  } catch {
    return undefined;
  }
}

function writeSnapshot(key: string, data: unknown): void {
  try {
    window.localStorage.setItem(key, JSON.stringify(data));
  } catch {
    // Full or blocked storage only costs the snapshot; the screen itself is unaffected.
  }
}

function clearSnapshot(key: string): void {
  try {
    window.localStorage.removeItem(key);
  } catch {
    // As above.
  }
}

/** The build the page runs: its entry script, `/assets/index-<hash>.js`, new with every deploy. */
function entryScript(doc: Document): string | null {
  return doc.querySelector('script[type="module"][src]')?.getAttribute('src') ?? null;
}

/**
 * Reloads the screen when a new version is deployed. A wall screen keeps its page for days, and
 * the old code went on drawing - a welcome text it did not know about never showed (26-10-2026).
 * Every `TV_DASHBOARD_REFRESH_MS` it reads index.html and reloads if that points at another build;
 * the snapshot puts the numbers straight back. Only on a good answer: a reload while the server is
 * down would leave the browser's error page on the wall. Not under `vite dev`, which has no build.
 */
function useReloadOnNewBuild(): void {
  useEffect(() => {
    const loaded = entryScript(document);
    if (import.meta.env.DEV || !loaded) return;
    const timer = window.setInterval(() => {
      void fetch('/index.html', { cache: 'no-store' })
        .then((response) => (response.ok ? response.text() : null))
        .then((html) => {
          const latest = html && entryScript(new DOMParser().parseFromString(html, 'text/html'));
          if (latest && latest !== loaded) window.location.reload();
        })
        .catch(() => undefined);
    }, TV_DASHBOARD_REFRESH_MS);
    return () => window.clearInterval(timer);
  }, []);
}

/** Older than the API's own limit for a yellow dot - typically a snapshot from before a reload. */
function isOld(updatedAt: string): boolean {
  return Date.now() - Date.parse(updatedAt) > TV_DASHBOARD_STALE_MINUTES * 60_000;
}

export function TvDashboardPage() {
  return (
    <TvScreenPage<TvDashboardResponse>
      screen="growth"
      apiPath="/dashboard/tv"
      fields={RESPONSE_FIELDS}
      render={(data, stale) => <TvDashboardView data={data} stale={stale} />}
    />
  );
}

/**
 * A wall screen: the token from the address, the numbers from `apiPath` every five minutes, the
 * snapshot in the browser, and "Opdater skærmen nu". `render` draws the numbers; `stale` is set
 * when the API says a source is out, a call failed, or the numbers are from before a reload.
 */
export function TvScreenPage<T extends TvScreenData>({
  screen,
  apiPath,
  fields,
  render,
  className,
}: {
  screen: TvScreen;
  apiPath: string;
  fields: readonly string[];
  render: (data: T, stale: boolean) => ReactNode;
  /** The stage's background, when not storskaerm 1's. */
  className?: string;
}) {
  useReloadOnNewBuild();
  const [params] = useSearchParams();
  const token = params.get('token')?.trim() ?? '';
  const key = snapshotKey(screen);

  const { data, error, isError, refetch } = useQuery({
    queryKey: ['tv-dashboard', screen, token],
    queryFn: () =>
      apiRequest<T>(apiPath, {
        headers: { 'X-Dashboard-Token': token },
        sessionless: true,
      }),
    enabled: token.length > 0,
    refetchInterval: TV_DASHBOARD_REFRESH_MS,
    // Skaermen har sjaeldent fokus, og siden skal opdatere sig selv alligevel.
    refetchIntervalInBackground: true,
    /*
     * De seneste tal fra browseren, til de nye er hentet. Tidsstemplet 0 goer dem foraeldede, saa
     * de hentes forfra med det samme - de staar kun, til svaret kommer.
     */
    initialData: () => readSnapshot<T>(key, fields),
    initialDataUpdatedAt: 0,
    // Aldrig ud af cachen: saa laenge siden er aaben, er der tal at vise.
    gcTime: Infinity,
  });

  /*
   * En afvisning er ikke en forbigaaende fejl. Er tokenet skiftet eller IP'en en anden, skal
   * skaermen sige det - ikke blive ved med at vise tal, den ikke laengere har adgang til.
   * Intervallet koerer videre, saa skaermen kommer tilbage af sig selv, naar det er rettet.
   */
  const denied = error instanceof ApiError && (error.status === 401 || error.status === 403);

  useEffect(() => {
    if (denied) clearSnapshot(key);
    else if (data) writeSnapshot(key, data);
  }, [data, denied, key]);

  /* Has an admin pressed "Opdater skærmen nu"? One timestamp - see the comment at the top. */
  const { data: refresh } = useQuery({
    queryKey: ['tv-dashboard', 'refresh', token],
    queryFn: () =>
      apiRequest<TvRefreshStatus>('/dashboard/tv/refresh', {
        headers: { 'X-Dashboard-Token': token },
        sessionless: true,
      }),
    enabled: token.length > 0,
    refetchInterval: TV_DASHBOARD_REFRESH_CHECK_MS,
    refetchIntervalInBackground: true,
    // The next question is ten seconds away; retrying sooner tells nothing new.
    retry: false,
  });

  const requestedAt = refresh?.requested_at ?? null;
  const generatedAt = data?.generated_at ?? null;

  /*
   * Fetch once the request is newer than the numbers. The new numbers are built after the request,
   * so their `generated_at` is later, and the condition does not hold again until the next press.
   */
  useEffect(() => {
    if (requestedAt && generatedAt && Date.parse(requestedAt) > Date.parse(generatedAt)) {
      void refetch();
    }
  }, [requestedAt, generatedAt, refetch]);

  let content: ReactNode;
  if (token.length === 0) {
    const info = TV_SCREEN_INFO[screen];
    content = (
      <Notice title="Adressen mangler et token">
        Åbn siden som <span className="font-mono text-tv-ink">{info.path}?token=…</span>. Tokenet
        står som {info.token_setting} på /admin/integrationer — eller brug "Kopier URL" på
        /admin/tv-dashboard.
      </Notice>
    );
  } else if (refresh?.override && !denied) {
    /* The welcome text or video, or a birthday, over every screen - see tv-welcome.ts. */
    content =
      'url' in refresh.override ? (
        <TvFrameView url={refresh.override.url} />
      ) : (
        <TvWelcomeView text={refresh.override.text} />
      );
  } else if (denied) {
    const ip = typeof error.details?.['ip'] === 'string' ? error.details['ip'] : null;
    content = (
      <Notice title="Ingen adgang">
        {error.message}
        {ip ? (
          <span className="mt-3 block">
            Skærmens adresse er <span className="font-mono text-tv-ink">{ip}</span>.
          </span>
        ) : null}
      </Notice>
    );
  } else if (data) {
    // Fejler et kald, bliver de seneste tal staaende med gul prik - B18: aldrig en tom skaerm.
    content = render(data, data.stale || isError || isOld(data.updated_at));
  } else if (isError) {
    content = (
      <Notice title="Kunne ikke hente tallene">Skærmen prøver igen om fem minutter.</Notice>
    );
  } else {
    content = <Notice title="Henter tal …" />;
  }

  // The zoom arrives with the numbers. Until they do, the screen is drawn at 100.
  return (
    <TvStage zoomPct={data?.display.zoom_pct ?? TV_ZOOM_DEFAULT_PCT} className={className}>
      {content}
    </TvStage>
  );
}

/**
 * The stage: 1920 x 1080, scaled to fit its frame and centered.
 *
 * The zoom scales the whole picture on top of that, around the center - see
 * `TV_ZOOM_DEFAULT_PCT`. Nothing reflows. Below 100 the picture shrinks and the page color shows
 * as a border around it; above 100 it grows and the frame cuts its edges off.
 *
 * The frame is the browser window on the screen. The preview on /admin/tv-dashboard passes the
 * size of its own box instead, and the stage then sits inside that box.
 */
export function TvStage({
  children,
  zoomPct,
  frame,
  className,
}: {
  children: ReactNode;
  zoomPct: number;
  frame?: { width: number; height: number };
  /** The page color, when not storskaerm 1's - storskaerm 2 and 3 are darker. */
  className?: string;
}) {
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  useEffect(() => {
    const update = (): void =>
      setWindowSize({ width: window.innerWidth, height: window.innerHeight });
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  const viewport = frame ?? windowSize;
  const fit = Math.min(viewport.width / STAGE_WIDTH, viewport.height / STAGE_HEIGHT);
  const scale = (fit * zoomPct) / 100;
  // Negative when zoomed in: the picture then starts outside the frame and is cropped evenly.
  const left = (viewport.width - STAGE_WIDTH * scale) / 2;
  const top = (viewport.height - STAGE_HEIGHT * scale) / 2;

  return (
    <div
      className={cn(
        'inset-0 overflow-hidden bg-tv-page font-tv text-tv-ink',
        frame ? 'absolute' : 'fixed cursor-none',
        className,
      )}
    >
      <div
        className="absolute origin-top-left px-8 py-7"
        style={{
          width: STAGE_WIDTH,
          height: STAGE_HEIGHT,
          transform: `translate(${left}px, ${top}px) scale(${scale})`,
        }}
      >
        {children}
      </div>
    </div>
  );
}

/**
 * A screen at 16:9 in a box - the previews on /admin/tv-dashboard. The box is always rendered, so
 * its size is known before the numbers arrive; the stage is drawn into it once they do.
 */
export function TvPreview({
  ready,
  failed,
  zoomPct,
  className,
  children,
}: {
  ready: boolean;
  failed: boolean;
  zoomPct: number;
  /** The screen's page color - storskaerm 2 and 3 are darker than 1. */
  className?: string;
  children: ReactNode;
}) {
  const [ref, size] = useElementSize<HTMLDivElement>();

  return (
    <div
      ref={ref}
      className={cn(
        'relative aspect-video w-full overflow-hidden rounded-md bg-tv-page',
        className,
      )}
    >
      {ready && size ? (
        <TvStage zoomPct={zoomPct} frame={size} className={className}>
          {children}
        </TvStage>
      ) : failed ? (
        <p className="absolute inset-0 flex items-center justify-center p-4 text-center text-sm text-tv-soft">
          Kunne ikke hente skærmens tal til forhåndsvisningen.
        </p>
      ) : (
        <p className="absolute inset-0 flex items-center justify-center text-sm text-tv-soft">
          Henter skærmens tal …
        </p>
      )}
    </div>
  );
}

const WELCOME_MIN_PX = 40;
const WELCOME_MAX_PX = 220;
/** Down to this size, each line as typed stays one line. Below it, long lines wrap instead. */
const WELCOME_UNWRAPPED_MIN_PX = 96;

/**
 * The welcome screen: one text, centered, as large as it fits on the stage - found by trying sizes
 * on the element itself, so any text, short or long, with or without line breaks, fills the
 * screen without running off it. Tried again once the font has loaded, since it changes the widths.
 *
 * **The lines as typed come first.** "Velkommen / EURODAN-HUSE AS" stays two lines, a little
 * smaller, rather than breaking the name at its hyphen. Only text too long for that at a readable
 * size wraps.
 */
export function TvWelcomeView({ text }: { text: string }) {
  const box = useRef<HTMLDivElement>(null);
  const line = useRef<HTMLParagraphElement>(null);
  const [fitted, setFitted] = useState({ size: WELCOME_MAX_PX, wrap: false });

  useLayoutEffect(() => {
    const fit = (): void => {
      const outer = box.current;
      const inner = line.current;
      if (!outer || !inner) return;
      const largest = (wrap: boolean): number => {
        inner.style.whiteSpace = wrap ? 'pre-line' : 'pre';
        let low = WELCOME_MIN_PX;
        let high = WELCOME_MAX_PX + 1;
        while (high - low > 1) {
          const mid = Math.floor((low + high) / 2);
          inner.style.fontSize = `${mid}px`;
          const fits =
            inner.scrollHeight <= outer.clientHeight && inner.scrollWidth <= outer.clientWidth;
          if (fits) low = mid;
          else high = mid;
        }
        return low;
      };
      const unwrapped = largest(false);
      const next =
        unwrapped >= WELCOME_UNWRAPPED_MIN_PX
          ? { size: unwrapped, wrap: false }
          : { size: largest(true), wrap: true };
      inner.style.whiteSpace = next.wrap ? 'pre-line' : 'pre';
      inner.style.fontSize = `${next.size}px`;
      setFitted(next);
    };
    fit();
    let cancelled = false;
    void document.fonts?.ready.then(() => {
      if (!cancelled) fit();
    });
    return () => {
      cancelled = true;
    };
  }, [text]);

  return (
    /* The margin on the outer box, so the measured one is exactly the room the text has. */
    <div className="h-full px-16 py-12">
      <div ref={box} className="flex h-full items-center justify-center overflow-hidden">
        <p
          ref={line}
          className="w-full text-center font-bold leading-[1.1]"
          style={{ fontSize: fitted.size, whiteSpace: fitted.wrap ? 'pre-line' : 'pre' }}
        >
          {text}
        </p>
      </div>
    </div>
  );
}

/**
 * The welcome screen's YouTube video over the whole stage, edge to edge. `url` is the embedded
 * player, from `youtubeEmbedUrl`. Sandboxed without top navigation, so nothing in the frame can
 * take the TV away from Codepass, where Stop would never reach it. The referrer is the origin
 * only - YouTube's player refuses to play without one, and the token must not leave.
 */
export function TvFrameView({ url }: { url: string }) {
  return (
    <iframe
      src={url}
      title="Fælles indhold"
      className="absolute inset-0 h-full w-full border-0"
      allow="autoplay; encrypted-media; picture-in-picture"
      sandbox="allow-scripts allow-same-origin allow-presentation"
      referrerPolicy="strict-origin-when-cross-origin"
    />
  );
}

function Notice({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className="flex h-full flex-col items-center justify-center text-center">
      <p className="text-[40px] font-semibold">{title}</p>
      {children ? <p className="mt-4 max-w-3xl text-xl text-tv-soft">{children}</p> : null}
    </div>
  );
}

/** "1 vunden sag", "23 vundne sager". */
function count(value: number, one: string, many: string): string {
  return `${formatInteger(value)} ${value === 1 ? one : many}`;
}

/**
 * Everything on the screen once the numbers are in. Also drawn by the preview on
 * /admin/tv-dashboard, which passes the autoscroll being tried instead of the saved one.
 */
export function TvDashboardView({
  data,
  stale,
  autoscroll = data.display.autoscroll ?? TV_AUTOSCROLL_DEFAULT,
}: {
  data: TvDashboardResponse;
  stale: boolean;
  autoscroll?: TvAutoscroll;
}) {
  return (
    <div className="flex h-full flex-col gap-[18px]">
      {/* Horisonten vokser fra venstre mod hoejre: maaned, kvartal, aar - B21. */}
      <div className="grid shrink-0 grid-cols-[5fr_4fr_4fr] gap-[18px]">
        <MonthCard month={data.month} />
        <QuarterCard quarter={data.quarter} />
        <YearCard year={data.year} updatedAt={data.updated_at} stale={stale} />
      </div>

      {/* Sales first, then delivery - the order since 01-10-2026. */}
      <div className="grid shrink-0 grid-cols-4 gap-[18px]">
        <PipelineKpi pipeline={data.pipeline} />
        <OrderIntakeKpi data={data} />
        <UtilizationKpi data={data} />
        <OrderBookKpi orderBook={data.order_book} />
      </div>

      {/*
       * Sales per seller and utilization per person side by side and as wide, since 22-10-2026
       * (Storskaerm-1-Aendringer-Spec.md). Top kunder went to storskaerm 2, win-rate gave its box
       * to "Klar til fakturering".
       */}
      <div className="grid min-h-0 flex-1 grid-cols-[1.55fr_1.55fr_1.05fr] gap-[18px]">
        <SalesPerSellerPanel sales={data.sales_per_seller} />
        <UtilizationPanel data={data} autoscroll={autoscroll} />
        <div className="flex min-h-0 flex-col gap-[18px]">
          <ReadyToInvoiceKpi ready={data.ready_to_invoice} />
          <RecurringRevenueKpi recurring={data.recurring_revenue} />
        </div>
      </div>
    </div>
  );
}

/**
 * The month, quarter and year cards. The top edge is accent, like the KPIs that answer "kan vi
 * levere?".
 */
function Card({ className, children }: { className?: string; children: ReactNode }) {
  return (
    <section
      className={cn(
        'flex flex-col rounded-2xl border-t-[6px] border-tv-accent bg-tv-card px-[30px] py-[22px]',
        className,
      )}
    >
      {children}
    </section>
  );
}

function Eyebrow({ children }: { children: ReactNode }) {
  return (
    <p className="text-[22px] font-semibold uppercase tracking-[0.1em] text-tv-soft">{children}</p>
  );
}

/**
 * En bjaelke paa kortets bund. `marker` er den hvide streg - hvor langt perioden er gaaet, og
 * dermed hvor bjaelken burde vaere, hvis maalet naas i et jaevnt tempo.
 */
function Bar({
  pct,
  marker,
  height,
  fill,
}: {
  pct: number | null;
  marker?: number;
  height: number;
  fill: string;
}) {
  return (
    <div className="relative overflow-hidden rounded-full bg-tv-page" style={{ height }}>
      <div
        className={cn('h-full rounded-full', fill)}
        style={{ width: `${Math.min(100, Math.max(0, pct ?? 0))}%` }}
      />
      {marker === undefined ? null : (
        <div
          className="absolute top-0 h-full w-[3px] bg-tv-ink"
          style={{ left: `${Math.min(100, Math.max(0, marker))}%` }}
        />
      )}
    </div>
  );
}

function Footer({ left, right }: { left: string; right: string }) {
  return (
    <div className="flex justify-between gap-6 text-[20px] text-tv-soft">
      <span className="shrink-0 font-semibold text-tv-ink">{left}</span>
      <span className="truncate">{right}</span>
    </div>
  );
}

/** Uden et maal: Business Central har intet budget for perioden. */
function reached(pct: number | null): string {
  return pct === null ? 'Intet mål sat' : `${formatPercent(pct)} nået`;
}

function MonthCard({ month }: { month: TvDashboardResponse['month'] }) {
  const billed = formatAmountParts(month.billed_excl_vat, { scale: 'thousand' });
  const target = formatAmountParts(month.target_excl_vat, { scale: 'thousand' });
  const previous = month.previous;
  const previousName = formatMonthName(previous.start);
  const previousBilled = formatAmountParts(previous.billed_excl_vat, { scale: 'thousand' });

  return (
    <Card className="gap-[10px]">
      <Eyebrow>{formatMonthName(month.start)} · faktureret</Eyebrow>
      <div className="flex items-baseline gap-[14px]">
        <span className="tabular text-[104px] font-bold leading-none text-tv-accent">
          {billed.value}
        </span>
        <span className="text-[44px] font-medium leading-none">
          {month.target_excl_vat === null ? billed.unit : `af ${target.value} ${target.unit}`}
        </span>
      </div>
      <Bar pct={month.pct_of_target} height={34} fill="bg-tv-accent" />
      <Footer
        left={reached(month.pct_of_target)}
        right={
          previous.pct_of_target === null
            ? `${previousName}: ${previousBilled.value} ${previousBilled.unit}`
            : `${previousName}: ${formatPercent(previous.pct_of_target)} af mål`
        }
      />
    </Card>
  );
}

function QuarterCard({ quarter }: { quarter: TvDashboardResponse['quarter'] }) {
  const million = { scale: 'million' as const };
  const billed = formatAmountParts(quarter.billed_excl_vat, million);
  const target = formatAmountParts(quarter.target_excl_vat, million);
  const wip = formatAmountParts(quarter.wip_excl_vat, { ...million, decimals: 2 });
  const wipText = `WIP ${wip.value} ${wip.unit} ufaktureret`;

  const remaining = quarter.remaining_to_target_excl_vat;
  let right = wipText;
  if (remaining !== null && remaining > 0) {
    const missing = formatAmountParts(remaining, million);
    right = `mangler ${missing.value} ${missing.unit} · ${wipText}`;
  } else if (remaining !== null) {
    right = `mål nået · ${wipText}`;
  }

  return (
    <Card className="gap-[10px]">
      <div className="flex items-baseline justify-between">
        <Eyebrow>Q{quarter.quarter} · faktureret</Eyebrow>
        <p className="text-[20px] text-tv-soft">
          {count(quarter.days_left, 'dag', 'dage')} tilbage
        </p>
      </div>
      <div className="flex items-baseline gap-4">
        <span className="tabular text-[104px] font-bold leading-none text-tv-accent">
          {billed.value}
        </span>
        <span className="text-[44px] font-medium leading-none">
          {quarter.target_excl_vat === null ? billed.unit : `af ${target.value} ${target.unit}`}
        </span>
      </div>
      <Bar
        pct={quarter.pct_of_target}
        marker={quarter.expected_pct}
        height={34}
        fill="bg-tv-accent"
      />
      <Footer left={reached(quarter.pct_of_target)} right={right} />
    </Card>
  );
}

const PACE_TEXT: Record<TvPace, string> = {
  ahead: 'Foran forventet',
  slightly_behind: 'Lidt bagud mod forventet',
  behind: 'Bagud mod forventet',
};

/**
 * The right-hand side of the year card's footer: how the year is doing against the white line -
 * "Bagud mod forventet 75 %". The backlog in hours stood here from 01-10-2026 and was removed
 * 09-10-2026; this is what the footer said before it.
 */
function yearFooterRight(year: TvDashboardResponse['year']): string {
  if (year.pace === null) return '';
  return `${PACE_TEXT[year.pace]} ${formatPercent(year.expected_pct)}`;
}

/**
 * The year, drawn like the month and quarter cards. This departs from B21 (year toned down):
 * decided 29-09-2026 that all three top cards look the same. The timestamp stays here (B26).
 */
function YearCard({
  year,
  updatedAt,
  stale,
}: {
  year: TvDashboardResponse['year'];
  updatedAt: string;
  stale: boolean;
}) {
  const million = { scale: 'million' as const };
  const billed = formatAmountParts(year.billed_excl_vat, million);
  const target = formatAmountParts(year.target_excl_vat, million);

  return (
    <Card className="gap-[10px]">
      <div className="flex items-baseline justify-between gap-4">
        <Eyebrow>Året {year.year} · faktureret</Eyebrow>
        <p className="shrink-0 text-[20px] text-tv-soft">
          {count(year.days_left, 'dag', 'dage')} tilbage
        </p>
      </div>
      <div className="flex items-baseline gap-4">
        <span className="tabular text-[104px] font-bold leading-none text-tv-accent">
          {billed.value}
        </span>
        <span className="text-[44px] font-medium leading-none">
          {year.target_excl_vat === null ? billed.unit : `af ${target.value} ${target.unit}`}
        </span>
      </div>
      <Bar pct={year.pct_of_target} marker={year.expected_pct} height={34} fill="bg-tv-accent" />
      <Footer left={reached(year.pct_of_target)} right={yearFooterRight(year)} />
      <p className="flex items-center gap-2 text-base text-tv-soft">
        <span
          className={cn(
            'inline-block h-2.5 w-2.5 rounded-full',
            stale ? 'bg-tv-stale' : 'bg-tv-accent',
          )}
          aria-hidden
        />
        Opdateret {formatDate(updatedAt)} · {formatTime(updatedAt)}
      </p>
    </Card>
  );
}

/**
 * A key figure. The top edge is accent on the cards that answer "kan vi levere?" - utilization
 * and the order book - and soft on those that answer "sælger vi nok?": pipeline, order intake and
 * recurring revenue. Amber on "Klar til fakturering" - money waiting (22-10-2026). Every box on the
 * screen has a top edge since 12-10-2026.
 */
function Kpi({
  label,
  value,
  unit,
  detail,
  tone,
  valueClassName,
  labelClassName,
  className,
  children,
}: {
  label: string;
  value: string;
  unit?: string;
  detail: string;
  tone: 'accent' | 'soft' | 'amber';
  valueClassName?: string;
  labelClassName?: string;
  className?: string;
  /** Lines under `detail`. */
  children?: ReactNode;
}) {
  return (
    <section
      className={cn(
        'flex flex-col gap-1.5 rounded-2xl border-t-[6px] bg-tv-card px-[26px] py-5',
        tone === 'accent'
          ? 'border-tv-accent'
          : tone === 'amber'
            ? 'border-tv-amber'
            : 'border-tv-soft',
        className,
      )}
    >
      <p
        className={cn(
          'text-[20px] font-semibold uppercase tracking-[0.08em] text-tv-soft',
          labelClassName,
        )}
      >
        {label}
      </p>
      <p className={cn('tabular text-[76px] font-bold leading-none', valueClassName)}>
        {value}
        {unit ? <span className="text-[38px] font-medium text-tv-ink"> {unit}</span> : null}
      </p>
      <p className="text-[20px] text-tv-soft">{detail}</p>
      {children}
    </section>
  );
}

/**
 * The house's utilization: billable hours only. The internal work beside it ("inkl. internt") went
 * to storskaerm 3 on 22-10-2026.
 */
function UtilizationKpi({ data }: { data: TvDashboardResponse }) {
  const { house_pct, previous_house_pct, target_pct } = data.utilization;
  const goal = `mål ${formatPercent(target_pct)}`;

  let detail = goal;
  if (house_pct !== null && previous_house_pct !== null) {
    const arrow = house_pct >= previous_house_pct ? '▲' : '▼';
    detail = `${arrow} ${formatPercent(previous_house_pct)} i ${formatMonthName(
      data.month.previous.start,
    )} · ${goal}`;
  }

  return (
    <Kpi
      label="Faktureringsgrad · samlet"
      value={formatPercent(house_pct)}
      detail={detail}
      tone="accent"
    />
  );
}

/**
 * The titles of the two cards in the bottom right column, a size under the others: at 20 px
 * "Løbende indtjening · faste ydelser" took two lines, and the column ran past the boxes beside it.
 */
const RIGHT_COLUMN_LABEL = 'text-[18px]';

/**
 * "Klar til fakturering": what could be invoiced today, in thousands without decimals - ready
 * hours and closed milestones not yet invoiced (Storskaerm-1-Aendringer-Spec.md, section 2). With
 * nothing ready, a green 0 and "Alt er faktureret".
 */
function ReadyToInvoiceKpi({ ready }: { ready: TvDashboardResponse['ready_to_invoice'] }) {
  const nothing = ready.amount_excl_vat === 0 && ready.milestone_count === 0 && ready.hours === 0;
  const amount = formatAmountParts(ready.amount_excl_vat, { scale: 'thousand' });
  const detail = nothing
    ? 'Alt er faktureret'
    : `${count(ready.milestone_count, 'milepæl', 'milepæle')} + ${count(
        Math.round(ready.hours),
        'time',
        'timer',
      )} · ${count(ready.contract_count, 'kontrakt', 'kontrakter')}`;

  return (
    <Kpi
      className="flex-1 justify-center"
      labelClassName={RIGHT_COLUMN_LABEL}
      label="Klar til fakturering"
      value={nothing ? '0' : amount.value}
      unit={nothing ? undefined : amount.unit}
      valueClassName={nothing ? 'text-tv-accent' : 'text-tv-amber'}
      detail={detail}
      tone="amber"
    >
      {nothing || ready.oldest_days === null ? null : (
        <p className="text-[20px] font-semibold text-tv-amber">
          Ældste har ventet {count(ready.oldest_days, 'dag', 'dage')}
        </p>
      )}
    </Kpi>
  );
}

function OrderIntakeKpi({ data }: { data: TvDashboardResponse }) {
  const { amount_excl_vat, won_count, new_customers } = data.order_intake;
  const amount = formatAmountParts(amount_excl_vat);

  return (
    <Kpi
      label="Ordreindgang YTD"
      value={amount.value}
      unit={amount.unit}
      detail={`${count(won_count, 'vunden sag', 'vundne sager')} · ${count(
        new_customers,
        'ny kunde',
        'nye kunder',
      )} i ${data.year.year}`}
      tone="soft"
    />
  );
}

/**
 * The full estimated value of the open deals. The response still carries the weighted figure, but
 * the screen doesn't show it: CRM has no probability on the open deals, so it read 0 kr
 * (decided 07-10-2026).
 */
function PipelineKpi({ pipeline }: { pipeline: TvDashboardResponse['pipeline'] }) {
  const amount = formatAmountParts(pipeline.unweighted_excl_vat);

  return (
    <Kpi
      label="Pipeline"
      value={amount.value}
      unit={amount.unit}
      detail={count(pipeline.open_count, 'åben sag', 'åbne sager')}
      tone="soft"
    />
  );
}

function OrderBookKpi({ orderBook }: { orderBook: TvDashboardResponse['order_book'] }) {
  const scope = `${count(orderBook.contract_count, 'projekt', 'projekter')} · ${count(
    orderBook.change_request_count,
    'CR',
    "CR'er",
  )}`;

  /* Arbejdsdagene kraever normtiden. Indtil den findes, staar ordrebogen i timer. */
  return orderBook.days === null ? (
    <Kpi
      label="Ordrebog · arbejde til"
      value={formatInteger(orderBook.hours)}
      unit="timer"
      detail={scope}
      tone="accent"
    />
  ) : (
    <Kpi
      label="Ordrebog · arbejde til"
      value={formatInteger(orderBook.days)}
      unit="arbejdsdage"
      detail={`${formatInteger(orderBook.hours)} timer · ${scope}`}
      tone="accent"
    />
  );
}

/**
 * "Løbende indtjening · faste ydelser": operations, hosting and licences on the signed
 * agreements, a month at a time. Always in thousands with one decimal, so 600 kr reads 0,6 and
 * not as a different unit from one refresh to the next.
 */
function RecurringRevenueKpi({
  recurring,
}: {
  recurring: TvDashboardResponse['recurring_revenue'];
}) {
  const thousand = { scale: 'thousand' as const, decimals: 1 };
  const monthly = formatAmountParts(recurring.monthly_excl_vat, thousand);
  const annual = formatAmountParts(recurring.annual_excl_vat, thousand);

  return (
    <Kpi
      className="flex-1 justify-center"
      labelClassName={RIGHT_COLUMN_LABEL}
      label="Løbende indtjening · faste ydelser"
      value={monthly.value}
      unit={`${monthly.unit}/md.`}
      detail={`Hosting, licenser, drift og serviceaftaler · ${annual.value} ${annual.unit} pr. år`}
      tone="soft"
    />
  );
}

/** A dot - or the target's line - and its word, at the bottom of a panel. */
function Legend({ items }: { items: Array<{ label: string; className: string }> }) {
  return (
    <p className="flex shrink-0 items-center gap-5 text-[18px] text-tv-soft">
      {items.map((item) => (
        <span key={item.label} className="flex items-center gap-2">
          <span
            className={cn('inline-block h-3.5 w-3.5 rounded-full', item.className)}
            aria-hidden
          />
          {item.label}
        </span>
      ))}
    </p>
  );
}

/** "333.500 kr." - whole kroner, as CRM's goal shows them. */
function kroner(value: number): string {
  const parts = formatAmountParts(value, { scale: 'kr' });
  return `${parts.value} ${parts.unit}`;
}

/** "−142.400 kr" - short of the budget, with the screens' real minus sign. */
function shortfall(value: number): string {
  return `${formatSigned(-value)} ${formatAmountParts(value, { scale: 'kr' }).unit}`;
}

/** The legend's mark for the target: the white line, upright. */
const TARGET_LEGEND = { label: 'Mål', className: 'w-1 bg-tv-ink' };

/** The target in a badge beside the box's title (Storskaerm-1-Maalvisning.md). */
function TargetBadge({ children }: { children: ReactNode }) {
  return (
    <span className="shrink-0 rounded-full border-2 border-tv-ink px-3 py-0.5 text-[15px] font-bold">
      {children}
    </span>
  );
}

/** Room above a bar for its "MÅL …" label: the label, a gap, and the line's 12 px above the bar. */
const TARGET_LABEL_ROOM_PX = 30;

/**
 * A 22 px bar with its target (Storskaerm-1-Maalvisning.md): a white line at `targetPct` of the
 * width, from 12 px above the bar to 6 px below it, with `label` centered above. Without a target
 * there is no line, and the label stands at the left as a note. `children` are the fills.
 */
function TargetBar({
  targetPct,
  label,
  children,
}: {
  targetPct: number | null;
  label: string;
  children: ReactNode;
}) {
  const left = targetPct === null ? undefined : `${barWidth(targetPct)}%`;
  return (
    <div className="relative min-w-0 flex-1" style={{ paddingTop: TARGET_LABEL_ROOM_PX }}>
      <p
        className={cn(
          'absolute top-0 whitespace-nowrap text-[14px] font-bold leading-none',
          targetPct === null ? 'left-0 font-medium text-tv-soft' : '-translate-x-1/2',
        )}
        style={{ left }}
      >
        {label}
      </p>
      <div className="relative h-[22px] rounded-full bg-tv-page">
        {children}
        {targetPct === null ? null : (
          <div
            className="absolute -bottom-[6px] -top-[12px] w-1 -translate-x-1/2 rounded-full bg-tv-ink"
            style={{ left }}
          />
        )}
      </div>
    </div>
  );
}

/** A seller's bar runs from 0 to 120 % of the budget, so it shows when they are over it. */
const SELLER_SCALE_PCT = 120;

/**
 * "Salg pr. sælger · Q4 2026": each seller's goal in CRM, as CRM has it. The budget is the white
 * line, won is accent and in progress is blue behind it, and under the percentage stands what is
 * left to the budget - or "Mål nået" (Storskaerm-1-Maalvisning.md, 24-10-2026; before, the bar was
 * 0-100 % of the budget without a line).
 *
 * **A fixed layout of `TV_SELLER_ROWS` rows.** Fewer sellers stand at the top and leave the rest
 * empty; the rows never move or grow. A seller CRM has two goals for shows "—".
 *
 * The seller furthest towards their own budget wears the crown, as on "Faktureringsgrad pr.
 * person" (26-10-2026). A tie shares it; while nobody has won anything this quarter, nobody has it.
 */
function SalesPerSellerPanel({ sales }: { sales: TvDashboardResponse['sales_per_seller'] }) {
  const rows = Array.from({ length: TV_SELLER_ROWS }, (_, index) => sales.sellers[index] ?? null);
  const shown = sales.sellers.length > 0;
  const top = Math.max(0, ...sales.sellers.map((seller) => seller.pct ?? 0));

  return (
    <section className="flex min-h-0 flex-col gap-3 rounded-2xl border-t-[6px] border-tv-amber bg-tv-card px-[26px] py-[18px]">
      <div className="flex items-center justify-between gap-4">
        <h2 className="text-[22px] font-semibold">
          Salg pr. sælger · Q{sales.quarter} {sales.year}
        </h2>
        {shown ? <TargetBadge>MÅL = kvartalsbudget</TargetBadge> : null}
      </div>

      {sales.missing_reason ? (
        <p className="flex flex-1 items-center justify-center text-center text-[24px] text-tv-soft">
          {sales.missing_reason}
        </p>
      ) : (
        <div className="grid min-h-0 flex-1 grid-rows-3">
          {rows.map((seller, index) =>
            seller ? (
              <SellerRow
                key={seller.name}
                seller={seller}
                crowned={top > 0 && seller.pct === top}
              />
            ) : (
              <div key={`tom-${index}`} aria-hidden />
            ),
          )}
        </div>
      )}

      <div className="flex shrink-0 items-center justify-between gap-4">
        {shown ? (
          <Legend
            items={[
              { label: 'Vundet', className: 'bg-tv-accent' },
              { label: 'I gang', className: 'bg-tv-progress' },
              TARGET_LEGEND,
            ]}
          />
        ) : (
          <span />
        )}
        {sales.rolled_up_at ? (
          <p className="text-[16px] text-tv-soft">
            CRM opdateret {formatDate(sales.rolled_up_at)} kl. {formatTime(sales.rolled_up_at)}
          </p>
        ) : null}
      </div>
    </section>
  );
}

function SellerRow({
  seller,
  crowned,
}: {
  seller: TvDashboardResponse['sales_per_seller']['sellers'][number];
  crowned: boolean;
}) {
  const budget = seller.budget_excl_vat;
  /* An amount's share of the bar: its percentage of the budget, on the 0-120 % scale. */
  const share = (value: number | null): number =>
    budget && value !== null ? barWidth(((value / budget) * 100 * 100) / SELLER_SCALE_PCT) : 0;
  const won = share(seller.won_excl_vat);
  /* Won and in progress together, stopped at the bar's end - the blue never reaches past it. */
  const reach = barWidth(won + share(seller.in_progress_excl_vat));
  /* typeof, not null: a snapshot saved before 26-10-2026 has no `remaining_excl_vat`. */
  const remaining =
    typeof seller.remaining_excl_vat === 'number' ? seller.remaining_excl_vat : null;

  return (
    <div className="flex items-end gap-[14px] self-center">
      <div className="relative flex h-[22px] w-[110px] shrink-0 items-center">
        <p className="truncate text-[22px] font-medium">{seller.name}</p>
        {crowned ? (
          <Crown className="absolute -left-[14px] -top-[16px] h-[22px] w-[28px] -rotate-[24deg] text-tv-crown" />
        ) : null}
      </div>
      <TargetBar
        targetPct={budget === null ? null : (100 * 100) / SELLER_SCALE_PCT}
        label={budget === null ? 'Flere aktive salgsmål i CRM' : `MÅL ${kroner(budget)}`}
      >
        <div
          className="absolute inset-y-0 left-0 rounded-full bg-tv-progress"
          style={{ width: `${reach}%` }}
        />
        <div className="relative h-full rounded-full bg-tv-accent" style={{ width: `${won}%` }} />
      </TargetBar>
      <div className="flex w-[180px] shrink-0 flex-col items-end gap-1.5">
        <p className="tabular text-[34px] font-bold leading-none">{formatPercent(seller.pct)}</p>
        {remaining === null ? null : (
          <p
            className={cn(
              'rounded-md px-3 py-[3px] text-[18px] font-bold leading-none text-tv-page',
              remaining > 0 ? 'bg-tv-amber' : 'bg-tv-accent',
            )}
          >
            {remaining > 0 ? shortfall(remaining) : 'Mål nået'}
          </p>
        )}
      </div>
    </div>
  );
}

/** Fra saa mange personer staar listen i to kolonner - B30. */
const TWO_COLUMNS_FROM = 10;

/**
 * The lowest a row may be before the list is too long for the panel and scrolls: the "MÅL …" label
 * and the 22 px bar make 52, and the line's 6 px under the bar needs the rest.
 */
const PERSON_ROW_MIN_PX = 56;
const PERSON_ROW_GAP_PX = 4;

type UtilizationPerson = TvDashboardResponse['utilization']['people'][number];

/**
 * Utilization per person. The list comes from Entra ID, not from the code, so a new hire shows up
 * by themselves (B30). The rows share the height evenly; the bar is always 26 px.
 *
 * **Er der flere, end der er plads til, ruller listen** - langsomt nedad og forfra i én uendelig
 * loekke, med en tom raekke mellem slutningen og begyndelsen. Hastigheden, eller at den er slaaet
 * fra, saettes paa /admin/tv-dashboard. Slaaet fra deler raekkerne hoejden som foer, hvor taet det
 * saa bliver.
 *
 * Stregen paa hver bjaelke er personens eget maal, with "MÅL 70 %" above it and "MÅL pr. person"
 * in a badge (Storskaerm-1-Maalvisning.md, 24-10-2026). The bar is billable hours only - accent at
 * or over the target, soft under it, as before the spec (kept 26-10-2026; the spec has it green).
 * The blue bar of internal work went to storskaerm 3 on 22-10-2026.
 */
function UtilizationPanel({
  data,
  autoscroll,
}: {
  data: TvDashboardResponse;
  autoscroll: TvAutoscroll;
}) {
  const { people, missing_reason } = data.utilization;
  const columns = people.length >= TWO_COLUMNS_FROM ? 2 : 1;
  const rows = Math.ceil(people.length / columns);

  // Altid tegnet, saa hoejden er maalt, foer der er personer at vise.
  const [viewport, size] = useElementSize<HTMLDivElement>();
  const listHeight = rows * PERSON_ROW_MIN_PX + Math.max(0, rows - 1) * PERSON_ROW_GAP_PX;
  const scrolling =
    autoscroll !== 'off' && people.length > 0 && size !== null && listHeight > size.height;

  return (
    <section className="flex min-h-0 flex-col gap-3 rounded-2xl border-t-[6px] border-tv-accent bg-tv-card px-[26px] py-[18px]">
      <div className="flex items-center justify-between gap-4">
        <h2 className="text-[22px] font-semibold">
          Faktureringsgrad · {formatMonthName(data.month.start)}
        </h2>
        {people.length > 0 ? <TargetBadge>MÅL pr. person</TargetBadge> : null}
      </div>

      {/* Reaches 16 px into the card's padding, where the crown hangs off the side of the name. */}
      <div ref={viewport} className="relative -ml-4 min-h-0 flex-1 overflow-hidden pl-4">
        {people.length === 0 ? (
          <p className="flex h-full items-center justify-center text-center text-[24px] text-tv-soft">
            {missing_reason}
          </p>
        ) : scrolling ? (
          <Autoscroll
            distance={listHeight + PERSON_ROW_MIN_PX}
            pxPerSecond={TV_AUTOSCROLL_PX_PER_SECOND[autoscroll]}
          >
            <PeopleGrid people={people} columns={columns} rows={rows} />
            {/* Kopien, der ruller ind nedefra, saa loekken ikke har et hul. */}
            <div aria-hidden style={{ marginTop: PERSON_ROW_MIN_PX }}>
              <PeopleGrid people={people} columns={columns} rows={rows} />
            </div>
          </Autoscroll>
        ) : (
          <PeopleGrid people={people} columns={columns} rows={rows} fill />
        )}
      </div>

      {people.length > 0 ? (
        <Legend items={[{ label: 'Fakturerbart', className: 'bg-tv-accent' }, TARGET_LEGEND]} />
      ) : null}
    </section>
  );
}

/** A percentage as a bar's width, 0-100. */
function barWidth(pct: number): number {
  return Number.isFinite(pct) ? Math.min(100, Math.max(0, pct)) : 0;
}

/**
 * Personerne i kolonner, fyldt kolonne for kolonne. `fill` deler panelets hoejde mellem
 * raekkerne; ellers har hver raekke den faste hoejde, som autoscroll regner med.
 *
 * The one furthest over their own target wears the crown - the first row, as the API orders them
 * (23-10-2026; before, the highest utilization). A tie shares it, and while nobody is over their
 * target - early in the month, say - nobody has it.
 */
function PeopleGrid({
  people,
  columns,
  rows,
  fill = false,
}: {
  people: UtilizationPerson[];
  columns: number;
  rows: number;
  fill?: boolean;
}) {
  /* Tenths of a point, so 69,7 - 70 compares equal for two people with the same figures. */
  const over = (person: UtilizationPerson): number =>
    Math.round((person.pct - person.target_pct) * 10);
  const top = Math.max(0, ...people.map(over));

  return (
    <div
      className={cn('grid grid-flow-col gap-x-[26px]', fill && 'h-full')}
      style={{
        rowGap: PERSON_ROW_GAP_PX,
        gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
        gridTemplateRows: fill
          ? `repeat(${rows}, minmax(0, 1fr))`
          : `repeat(${rows}, ${PERSON_ROW_MIN_PX}px)`,
      }}
    >
      {people.map((person) => (
        <div key={person.name} className="flex items-end gap-[14px] self-center">
          <div className="relative flex h-[22px] w-[110px] shrink-0 items-center">
            <p className="truncate text-[22px] font-medium">{person.name}</p>
            {top > 0 && over(person) === top ? (
              <Crown className="absolute -left-[14px] -top-[16px] h-[22px] w-[28px] -rotate-[24deg] text-tv-crown" />
            ) : null}
          </div>
          <TargetBar
            targetPct={person.target_pct}
            label={`MÅL ${formatPercent(person.target_pct)}`}
          >
            <div
              className={cn(
                'relative h-full rounded-full',
                person.pct >= person.target_pct ? 'bg-tv-accent' : 'bg-tv-soft',
              )}
              style={{ width: `${barWidth(person.pct)}%` }}
            />
          </TargetBar>
          {/* The spec's 180 px leaves no bar in two columns; the percentage needs 110. */}
          <p
            className={cn(
              'tabular shrink-0 text-right text-[34px] font-bold leading-none',
              columns === 2 ? 'w-[110px]' : 'w-[180px]',
            )}
          >
            {formatPercent(person.pct)}
          </p>
        </div>
      ))}
    </div>
  );
}

/**
 * The crown on the first place, tilted on the top left corner of the name with half of it off the
 * side. An SVG, not the emoji:
 * a kiosk browser does not always have an emoji font, and the color follows the screen's palette.
 */
function Crown({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 28 22" fill="currentColor" className={className} aria-hidden>
      <path d="M4 16 2.5 7 9 11.5 14 4l5 7.5L25.5 7 24 16Z" />
      <circle cx="2.5" cy="5.5" r="2" />
      <circle cx="14" cy="2.5" r="2" />
      <circle cx="25.5" cy="5.5" r="2" />
      <rect x="4" y="17.5" width="20" height="3" rx="1" />
    </svg>
  );
}

/**
 * Scrolls its children upwards by `distance` and starts over, without end. `distance` is the
 * height of one copy of the list plus the gap before the next, so the jump back to the start
 * lands on an identical picture and cannot be seen.
 *
 * The Web Animations API rather than a React loop: the browser runs it on the compositor, it
 * renders nothing per frame, and it can run for days on a wall screen without growing. A refresh
 * with the same number of people keeps the animation where it is; only a new distance or speed
 * starts it over.
 */
function Autoscroll({
  distance,
  pxPerSecond,
  children,
}: {
  distance: number;
  pxPerSecond: number;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element || typeof element.animate !== 'function') return;
    const animation = element.animate(
      [{ transform: 'translateY(0)' }, { transform: `translateY(-${distance}px)` }],
      { duration: (distance / pxPerSecond) * 1000, iterations: Infinity, easing: 'linear' },
    );
    return () => animation.cancel();
  }, [distance, pxPerSecond]);

  return (
    <div ref={ref} className="will-change-transform">
      {children}
    </div>
  );
}
